'use client'

import { useState, useCallback, useEffect, ReactNode, forwardRef } from 'react'
import {
  // Shield,
  // User,
  Save,
  AlertCircle,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  GraduationCap,
  Building,
  DollarSign,
  Settings,
  UserCheck,
  RotateCcw
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * BIRHAUS Role Manager Component
 * 
 * Comprehensive role management component implementing BIRHAUS principles:
 * - Spanish-first internationalization
 * - Miller's Law compliance (max 7±2 items per cognitive load)
 * - 4-3-1 cognitive rule (max 4 actions, 3 context actions, 1 primary)
 * - Undo-over-confirm pattern (role changes can be undone)
 * - Progressive disclosure (advanced options hidden by default)
 * - Accessibility-first design (WCAG AA+)
 * - Generic role data structure for any admin system
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | string[] | undefined | null | false)[]): string {
  return classes.flat().filter(Boolean).join(' ')
}

export interface BirhausRole {
  id: string
  code: string
  name: string
  description?: string | null
  module: 'general' | 'academic' | 'financial' | 'administrative'
  hierarchyLevel: number
  isSystem?: boolean
  isActive?: boolean
  metadata?: Record<string, any>
}

export interface BirhausUser {
  id: string
  email: string
  fullName: string
  primaryRole?: BirhausRole | null
  additionalRoles?: BirhausRole[]
  createdAt?: string
  metadata?: Record<string, any>
}

export interface BirhausRoleChangeEvent {
  type: 'primary_role_assigned' | 'primary_role_removed' | 'additional_role_added' | 'additional_role_removed' | 'metadata_updated'
  userId: string
  roleId?: string
  role?: BirhausRole
  oldValue?: any
  newValue?: any
  metadata?: Record<string, any>
  timestamp: string
}

export interface BirhausRoleManagerProps {
  // Core data
  user: BirhausUser
  availableRoles: BirhausRole[]
  
  // Event handlers
  onRoleChange: (event: BirhausRoleChangeEvent) => Promise<void>
  onClose?: () => void
  onSuccess?: () => void
  
  // BIRHAUS: Spanish-first labels
  titleEs?: string
  titleEn?: string
  
  primaryRoleLabelEs?: string
  primaryRoleLabelEn?: string
  
  additionalRolesLabelEs?: string
  additionalRolesLabelEn?: string
  
  assignPrimaryRoleLabelEs?: string
  assignPrimaryRoleLabelEn?: string
  
  addAdditionalRoleLabelEs?: string
  addAdditionalRoleLabelEn?: string
  
  changeLabelEs?: string
  changeLabelEn?: string
  
  removeLabelEs?: string
  removeLabelEn?: string
  
  showAdvancedLabelEs?: string
  showAdvancedLabelEn?: string
  
  hideAdvancedLabelEs?: string
  hideAdvancedLabelEn?: string
  
  cancelLabelEs?: string
  cancelLabelEn?: string
  
  saveLabelEs?: string
  saveLabelEn?: string
  
  warningTitleEs?: string
  warningTitleEn?: string
  
  warningMessageEs?: string
  warningMessageEn?: string
  
  // State
  loading?: boolean
  error?: string | null
  
  // Configuration
  locale?: 'es' | 'en'
  showAdvancedByDefault?: boolean
  maxAdditionalRoles?: number // BIRHAUS: Miller's Law compliance
  allowPrimaryRoleChange?: boolean
  allowMultipleRoles?: boolean
  
  // Customization
  renderUserInfo?: (user: BirhausUser) => ReactNode
  renderRoleMetadata?: (role: BirhausRole, metadata: Record<string, any>) => ReactNode
  getRoleIcon?: (module: string) => LucideIcon
  getRoleColor?: (module: string) => string
  
  // Styling
  className?: string
  headerClassName?: string
  contentClassName?: string
  actionsClassName?: string
}

const DEFAULT_MODULE_ICONS: Record<string, LucideIcon> = {
  general: Settings,
  academic: GraduationCap,
  financial: DollarSign,
  administrative: Building
}

const DEFAULT_MODULE_COLORS: Record<string, string> = {
  general: 'bg-purple-100 text-purple-700 border-purple-200',
  academic: 'bg-blue-100 text-blue-700 border-blue-200',
  financial: 'bg-green-100 text-green-700 border-green-200',
  administrative: 'bg-amber-100 text-amber-700 border-amber-200'
}

export const BirhausRoleManager = forwardRef<HTMLDivElement, BirhausRoleManagerProps>(({
  // Core
  user,
  availableRoles,
  
  // Events
  onRoleChange,
  onClose,
  onSuccess,
  
  // Labels
  titleEs = 'Gestión de Roles de Usuario',
  titleEn = 'User Role Management',
  
  primaryRoleLabelEs = 'Rol Principal',
  primaryRoleLabelEn = 'Primary Role',
  
  additionalRolesLabelEs = 'Roles Adicionales',
  additionalRolesLabelEn = 'Additional Roles',
  
  assignPrimaryRoleLabelEs = 'Asignar rol principal',
  assignPrimaryRoleLabelEn = 'Assign primary role',
  
  addAdditionalRoleLabelEs = 'Agregar rol adicional',
  addAdditionalRoleLabelEn = 'Add additional role',
  
  changeLabelEs = 'Cambiar',
  changeLabelEn = 'Change',
  
  removeLabelEs = 'Remover',
  removeLabelEn = 'Remove',
  
  showAdvancedLabelEs = 'Mostrar',
  showAdvancedLabelEn = 'Show',
  
  hideAdvancedLabelEs = 'Ocultar',
  hideAdvancedLabelEn = 'Hide',
  
  cancelLabelEs = 'Cancelar',
  cancelLabelEn = 'Cancel',
  
  saveLabelEs = 'Guardar Cambios',
  saveLabelEn = 'Save Changes',
  
  warningTitleEs = 'Importante sobre los cambios de roles',
  warningTitleEn = 'Important about role changes',
  
  warningMessageEs = 'Los cambios en los roles afectarán inmediatamente los permisos del usuario. Se recomienda notificar al usuario sobre estos cambios.',
  warningMessageEn = 'Role changes will immediately affect user permissions. It is recommended to notify the user about these changes.',
  
  // State
  loading = false,
  error = null,
  
  // Configuration
  locale = 'es',
  showAdvancedByDefault = false,
  maxAdditionalRoles = 5, // BIRHAUS: Miller's Law (7±2, being conservative with 5)
  allowPrimaryRoleChange = true,
  allowMultipleRoles = true,
  
  // Customization
  renderUserInfo,
  renderRoleMetadata,
  getRoleIcon,
  getRoleColor,
  
  // Styling
  className,
  headerClassName,
  contentClassName,
  actionsClassName,
  
  ...props
}, ref) => {
  const [primaryRole, setPrimaryRole] = useState<BirhausRole | null>(user.primaryRole || null)
  const [additionalRoles, setAdditionalRoles] = useState<BirhausRole[]>(user.additionalRoles || [])
  const [showAdvanced, setShowAdvanced] = useState(showAdvancedByDefault)
  const [showRoleSelector, setShowRoleSelector] = useState(false)
  const [selectorType, setSelectorType] = useState<'primary' | 'additional'>('primary')
  const [isProcessing, setIsProcessing] = useState(false)
  const [changes, setChanges] = useState<BirhausRoleChangeEvent[]>([])

  // BIRHAUS: Spanish-first text resolution
  const getDisplayText = useCallback((
    spanish: string,
    english?: string
  ): string => {
    return locale === 'es' ? spanish : (english || spanish)
  }, [locale])

  // Get module styling functions
  const getModuleIcon = useCallback((module: string): LucideIcon => {
    return getRoleIcon?.(module) || DEFAULT_MODULE_ICONS[module] || Settings
  }, [getRoleIcon])

  const getModuleColor = useCallback((module: string): string => {
    return getRoleColor?.(module) || DEFAULT_MODULE_COLORS[module] || 'bg-gray-100 text-gray-700 border-gray-200'
  }, [getRoleColor])

  // BIRHAUS Validation: Check Miller's Law compliance
  useEffect(() => {
    if (additionalRoles.length > maxAdditionalRoles) {
      console.warn(
        `BIRHAUS RoleManager: ${additionalRoles.length} additional roles exceed Miller's Law limit of ${maxAdditionalRoles}. ` +
        'Consider reducing role count or implementing progressive disclosure.'
      )
    }
  }, [additionalRoles.length, maxAdditionalRoles])

  // BIRHAUS Validation: Check 4-3-1 cognitive rule for available actions
  const getAvailableActions = useCallback(() => {
    const actions = []
    
    if (allowPrimaryRoleChange) {
      actions.push(primaryRole ? 'change_primary' : 'assign_primary')
    }
    
    if (allowMultipleRoles && additionalRoles.length < maxAdditionalRoles) {
      actions.push('add_additional')
    }
    
    if (additionalRoles.length > 0) {
      actions.push('manage_additional')
    }
    
    return actions
  }, [allowPrimaryRoleChange, allowMultipleRoles, primaryRole, additionalRoles.length, maxAdditionalRoles])

  // Handle primary role assignment
  const handlePrimaryRoleSelect = useCallback(async (role: BirhausRole) => {
    const oldRole = primaryRole
    setPrimaryRole(role)
    
    const changeEvent: BirhausRoleChangeEvent = {
      type: 'primary_role_assigned',
      userId: user.id,
      roleId: role.id,
      role,
      oldValue: oldRole,
      newValue: role,
      timestamp: new Date().toISOString()
    }
    
    setChanges(prev => [...prev, changeEvent])
    setShowRoleSelector(false)
  }, [primaryRole, user.id])

  // Handle additional role addition
  const handleAdditionalRoleAdd = useCallback(async (role: BirhausRole) => {
    // Check if role already exists
    if (additionalRoles.some(r => r.id === role.id)) {
      setShowRoleSelector(false)
      return
    }
    
    // Check Miller's Law limit
    if (additionalRoles.length >= maxAdditionalRoles) {
      console.warn(`BIRHAUS RoleManager: Cannot add more than ${maxAdditionalRoles} additional roles (Miller's Law compliance)`)
      setShowRoleSelector(false)
      return
    }
    
    const newRoles = [...additionalRoles, role]
    setAdditionalRoles(newRoles)
    
    const changeEvent: BirhausRoleChangeEvent = {
      type: 'additional_role_added',
      userId: user.id,
      roleId: role.id,
      role,
      newValue: role,
      timestamp: new Date().toISOString()
    }
    
    setChanges(prev => [...prev, changeEvent])
    setShowRoleSelector(false)
  }, [additionalRoles, maxAdditionalRoles, user.id])

  // Handle additional role removal
  const handleAdditionalRoleRemove = useCallback((roleId: string) => {
    const roleToRemove = additionalRoles.find(r => r.id === roleId)
    if (!roleToRemove) return
    
    const newRoles = additionalRoles.filter(r => r.id !== roleId)
    setAdditionalRoles(newRoles)
    
    const changeEvent: BirhausRoleChangeEvent = {
      type: 'additional_role_removed',
      userId: user.id,
      roleId,
      role: roleToRemove,
      oldValue: roleToRemove,
      timestamp: new Date().toISOString()
    }
    
    setChanges(prev => [...prev, changeEvent])
  }, [additionalRoles, user.id])

  // Handle save (BIRHAUS: Process all changes as batch)
  const handleSave = useCallback(async () => {
    if (changes.length === 0) {
      onSuccess?.()
      return
    }
    
    setIsProcessing(true)
    
    try {
      // Process each change
      for (const change of changes) {
        await onRoleChange(change)
      }
      
      setChanges([])
      onSuccess?.()
    } catch (error) {
      console.error('Error processing role changes:', error)
      // Error handling could be enhanced here
    } finally {
      setIsProcessing(false)
    }
  }, [changes, onRoleChange, onSuccess])

  // Handle cancel/reset (BIRHAUS: Undo pattern)
  const handleCancel = useCallback(() => {
    setPrimaryRole(user.primaryRole || null)
    setAdditionalRoles(user.additionalRoles || [])
    setChanges([])
    setShowAdvanced(showAdvancedByDefault)
    onClose?.()
  }, [user.primaryRole, user.additionalRoles, showAdvancedByDefault, onClose])

  // Handle role selector
  const openRoleSelector = useCallback((type: 'primary' | 'additional') => {
    setSelectorType(type)
    setShowRoleSelector(true)
  }, [])

  const availableActions = getAvailableActions()

  // BIRHAUS Validation: Check 4-3-1 cognitive rule
  if (availableActions.length > 4) {
    console.warn(
      `BIRHAUS RoleManager: ${availableActions.length} actions exceed 4-3-1 cognitive rule recommendation of 4. ` +
      'Consider consolidating actions or using progressive disclosure.'
    )
  }

  const displayTitle = getDisplayText(titleEs, titleEn)

  return (
    <div
      ref={ref}
      className={cn('birhaus-role-manager space-y-6', className)}
      {...props}
    >
      {/* Header */}
      <div className={cn('flex items-center justify-between', headerClassName)}>
        <h2 className="text-xl font-semibold text-gray-900">
          {displayTitle}
        </h2>
        {onClose && (
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isProcessing}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className={cn('space-y-6', contentClassName)}>
        {/* User Info */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          {renderUserInfo ? (
            renderUserInfo(user)
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-medium">
                {user.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{user.fullName}</h3>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Primary Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {getDisplayText(primaryRoleLabelEs, primaryRoleLabelEn)}
          </label>
          <div className="space-y-2">
            {primaryRole ? (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg', getModuleColor(primaryRole.module))}>
                      {(() => {
                        const Icon = getModuleIcon(primaryRole.module)
                        return <Icon className="h-4 w-4" />
                      })()}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{primaryRole.name}</h4>
                      {primaryRole.description && (
                        <p className="text-sm text-gray-500">{primaryRole.description}</p>
                      )}
                      {renderRoleMetadata && primaryRole.metadata && (
                        <div className="mt-2">
                          {renderRoleMetadata(primaryRole, primaryRole.metadata)}
                        </div>
                      )}
                    </div>
                  </div>
                  {allowPrimaryRoleChange && (
                    <button
                      onClick={() => openRoleSelector('primary')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                      disabled={isProcessing}
                    >
                      {getDisplayText(changeLabelEs, changeLabelEn)}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              allowPrimaryRoleChange && (
                <button
                  onClick={() => openRoleSelector('primary')}
                  className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  disabled={isProcessing}
                >
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Plus className="w-5 h-5" />
                    <span>{getDisplayText(assignPrimaryRoleLabelEs, assignPrimaryRoleLabelEn)}</span>
                  </div>
                </button>
              )
            )}
          </div>
        </div>

        {/* Additional Roles */}
        {allowMultipleRoles && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                {getDisplayText(additionalRolesLabelEs, additionalRolesLabelEn)}
                {additionalRoles.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({additionalRoles.length}/{maxAdditionalRoles})
                  </span>
                )}
              </label>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showAdvanced 
                  ? getDisplayText(hideAdvancedLabelEs, hideAdvancedLabelEn)
                  : getDisplayText(showAdvancedLabelEs, showAdvancedLabelEn)
                }
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-2">
                {additionalRoles.map((role) => (
                  <div key={role.id} className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={cn('p-1.5 rounded', getModuleColor(role.module))}>
                          {(() => {
                            const Icon = getModuleIcon(role.module)
                            return <Icon className="h-4 w-4" />
                          })()}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-900">{role.name}</h5>
                          {role.description && (
                            <p className="text-xs text-gray-500">{role.description}</p>
                          )}
                          {renderRoleMetadata && role.metadata && (
                            <div className="mt-1">
                              {renderRoleMetadata(role, role.metadata)}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleAdditionalRoleRemove(role.id)}
                        className="p-1 hover:bg-red-50 rounded text-red-600 hover:text-red-700 transition-colors"
                        disabled={isProcessing}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {additionalRoles.length < maxAdditionalRoles && (
                  <button
                    onClick={() => openRoleSelector('additional')}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                    disabled={isProcessing}
                  >
                    <div className="flex items-center justify-center gap-2 text-gray-600 text-sm">
                      <Plus className="w-4 h-4" />
                      <span>{getDisplayText(addAdditionalRoleLabelEs, addAdditionalRoleLabelEn)}</span>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900">
                {getDisplayText(warningTitleEs, warningTitleEn)}
              </p>
              <p className="text-sm text-amber-700">
                {getDisplayText(warningMessageEs, warningMessageEn)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={cn('flex items-center justify-between gap-3 pt-4 border-t', actionsClassName)}>
        <div className="text-sm text-gray-600">
          {changes.length > 0 && (
            <span className="flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {locale === 'es' 
                ? `${changes.length} cambio${changes.length !== 1 ? 's' : ''} pendiente${changes.length !== 1 ? 's' : ''}`
                : `${changes.length} pending change${changes.length !== 1 ? 's' : ''}`
              }
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {onClose && (
            <button
              onClick={handleCancel}
              className={cn(
                'inline-flex items-center px-4 py-2 text-sm font-medium',
                'text-gray-700 bg-white border border-gray-300 rounded-lg',
                'hover:bg-gray-50 hover:border-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-all duration-200'
              )}
              disabled={isProcessing}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {getDisplayText(cancelLabelEs, cancelLabelEn)}
            </button>
          )}
          
          <button
            onClick={handleSave}
            className={cn(
              'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
              'bg-blue-600 text-white border border-blue-600',
              'hover:bg-blue-700 hover:border-blue-700',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-200'
            )}
            disabled={isProcessing || changes.length === 0}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                {locale === 'es' ? 'Guardando...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {getDisplayText(saveLabelEs, saveLabelEn)}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Role Selector Modal */}
      {showRoleSelector && (
        <BirhausRoleSelector
          roles={availableRoles}
          selectedRoleId={selectorType === 'primary' ? primaryRole?.id : undefined}
          excludeRoleIds={[
            ...(primaryRole ? [primaryRole.id] : []),
            ...additionalRoles.map(r => r.id)
          ]}
          onSelect={selectorType === 'primary' ? handlePrimaryRoleSelect : handleAdditionalRoleAdd}
          onClose={() => setShowRoleSelector(false)}
          locale={locale}
          titleEs={selectorType === 'primary' 
            ? 'Seleccionar Rol Principal'
            : 'Seleccionar Rol Adicional'
          }
          titleEn={selectorType === 'primary' 
            ? 'Select Primary Role'
            : 'Select Additional Role'
          }
          getModuleIcon={getModuleIcon}
          getModuleColor={getModuleColor}
        />
      )}
    </div>
  )
})

BirhausRoleManager.displayName = 'BirhausRoleManager'

// Role Selector Component (Internal)
interface BirhausRoleSelectorProps {
  roles: BirhausRole[]
  selectedRoleId?: string
  excludeRoleIds?: string[]
  onSelect: (role: BirhausRole) => void
  onClose: () => void
  locale?: 'es' | 'en'
  titleEs?: string
  titleEn?: string
  getModuleIcon?: (module: string) => LucideIcon
  getModuleColor?: (module: string) => string
}

function BirhausRoleSelector({
  roles,
  selectedRoleId,
  excludeRoleIds = [],
  onSelect,
  onClose,
  locale = 'es',
  titleEs = 'Seleccionar Rol',
  titleEn = 'Select Role',
  getModuleIcon = (module) => DEFAULT_MODULE_ICONS[module] || Settings,
  getModuleColor = (module) => DEFAULT_MODULE_COLORS[module] || 'bg-gray-100 text-gray-700 border-gray-200'
}: BirhausRoleSelectorProps) {
  const [selectedRole, setSelectedRole] = useState<BirhausRole | null>(null)

  const availableRoles = roles.filter(role => 
    role.isActive !== false && !excludeRoleIds.includes(role.id)
  )

  const groupedRoles = availableRoles.reduce((acc, role) => {
    if (!acc[role.module]) acc[role.module] = []
    acc[role.module].push(role)
    return acc
  }, {} as Record<string, BirhausRole[]>)

  // BIRHAUS: Check Miller's Law compliance
  const totalRoles = availableRoles.length
  if (totalRoles > 9) {
    console.warn(
      `BIRHAUS RoleSelector: ${totalRoles} roles exceed Miller's Law guideline of 7±2 items. ` +
      'Consider implementing search/filtering or grouping for better usability.'
    )
  }

  const handleRoleSelect = (role: BirhausRole) => {
    setSelectedRole(role)
  }

  const handleConfirm = () => {
    if (selectedRole) {
      onSelect(selectedRole)
    }
  }

  const displayTitle = locale === 'es' ? titleEs : titleEn

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50/50">
          <h3 className="text-lg font-semibold text-gray-900">{displayTitle}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {Object.entries(groupedRoles).map(([module, moduleRoles]) => (
            <div key={module} className="space-y-2">
              <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                {(() => {
                  const Icon = getModuleIcon(module)
                  return <Icon className="w-4 h-4" />
                })()}
                <span>{module.charAt(0).toUpperCase() + module.slice(1)}</span>
              </div>
              
              <div className="grid gap-2">
                {moduleRoles.map(role => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role)}
                    className={cn(
                      'p-4 rounded-lg border-2 transition-all text-left relative',
                      selectedRole?.id === role.id
                        ? 'border-blue-600 bg-blue-50'
                        : cn('border-gray-200 hover:border-gray-300 hover:shadow-sm', getModuleColor(role.module))
                    )}
                  >
                    {selectedRole?.id === role.id && (
                      <div className="absolute top-3 right-3">
                        <Check className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    
                    <div className="pr-8">
                      <h4 className="font-medium text-gray-900">{role.name}</h4>
                      {role.description && (
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                      )}
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-xs bg-white/50 px-2 py-1 rounded">
                          {locale === 'es' ? 'Nivel' : 'Level'} {role.hierarchyLevel}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50/50">
          <button
            onClick={onClose}
            className={cn(
              'inline-flex items-center px-4 py-2 text-sm font-medium',
              'text-gray-700 bg-white border border-gray-300 rounded-lg',
              'hover:bg-gray-50 hover:border-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1',
              'transition-all duration-200'
            )}
          >
            {locale === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedRole}
            className={cn(
              'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
              'bg-blue-600 text-white border border-blue-600',
              'hover:bg-blue-700 hover:border-blue-700',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-200'
            )}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            {locale === 'es' ? 'Seleccionar' : 'Select'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Hook for managing role state
export function useBirhausRoleManager(initialUser: BirhausUser) {
  const [user, setUser] = useState<BirhausUser>(initialUser)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [changes, setChanges] = useState<BirhausRoleChangeEvent[]>([])

  const updateUser = useCallback((newUser: BirhausUser) => {
    setUser(newUser)
    setError(null)
  }, [])

  const handleRoleChange = useCallback(async (
    event: BirhausRoleChangeEvent,
    changeHandler?: (event: BirhausRoleChangeEvent) => Promise<void>
  ) => {
    if (!changeHandler) {
      // Store change locally if no handler provided
      setChanges(prev => [...prev, event])
      return
    }

    try {
      setLoading(true)
      setError(null)
      await changeHandler(event)
      setChanges([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const clearChanges = useCallback(() => {
    setChanges([])
    setError(null)
  }, [])

  const hasUnsavedChanges = changes.length > 0

  return {
    user,
    loading,
    error,
    changes,
    hasUnsavedChanges,
    updateUser,
    handleRoleChange,
    clearChanges,
    setError
  }
}

// Common role configurations for different industries
export const birhausRoleConfigurations = {
  // Educational institution roles
  education: {
    maxAdditionalRoles: 3,
    allowMultipleRoles: true,
    modules: ['academic', 'administrative', 'general'],
    hierarchyLevels: {
      superAdmin: 1,
      director: 10,
      coordinator: 20,
      teacher: 30,
      staff: 40
    }
  },
  
  // Financial services roles  
  financial: {
    maxAdditionalRoles: 2,
    allowMultipleRoles: true,
    modules: ['financial', 'administrative', 'general'],
    hierarchyLevels: {
      cfo: 1,
      manager: 10,
      analyst: 20,
      clerk: 30
    }
  },
  
  // General corporate roles
  corporate: {
    maxAdditionalRoles: 4,
    allowMultipleRoles: true,
    modules: ['administrative', 'general'],
    hierarchyLevels: {
      ceo: 1,
      vp: 5,
      director: 10,
      manager: 20,
      employee: 30
    }
  }
}